<template>
  <div class="payment-create main-content">
    <div class="page-header">
      <h3 class="page-title">
        {{ $t("islamic_finance.invoice_verification") }}
      </h3>
      <ol class="breadcrumb">
        <li class="breadcrumb-item">
          <router-link slot="item-title" to="dashboard">
            {{ $t("general.home") }}
          </router-link>
        </li>
        <li v-if="userType === 'supplier'" class="breadcrumb-item">
          <router-link slot="item-title" to="/supplier/funders">
            {{ $tc("funders.finance", 2) }}
          </router-link>
        </li>
        <li class="breadcrumb-item">
          <router-link
            v-if="userType === 'supplier'"
            slot="item-title"
            :to="{
              name: 'FunderFinance',
              params: { id: this.$route.params.funder_id }
            }"
          >
            {{ $t("islamic_finance.title") }}
          </router-link>
        </li>
        <li class="breadcrumb-item">
          <router-link slot="item-title" to="#">
            {{ $t("islamic_finance.invoice_verification") }}
          </router-link>
        </li>
      </ol>

      <div v-if="selectedPreapprovedInvoice > 0" class="page-actions row">
        <div class="col-xs-2 mr-4">
          <base-button
            size="large"
            icon="save"
            color="theme"
            @click="saveInvoiceInfo"
          >
            {{ $t("islamic_finance.invoice_verified") }}
          </base-button>
        </div>
      </div>
    </div>
    <single-funder-row
      v-if="userType == 'supplier'"
      :fetch-data="fetchDataFunder"
    />
    <single-islamic-finance-row :fetch-data="fetchData" />
    <div class="custom-control" />
    <table-component
      ref="table"
      :show-filter="false"
      :data="fetchPreapprovedInvoiceData"
      table-class="table"
    >
      <table-column :sortable="false" :filterable="false" cell-class="no-click">
        <template slot-scope="row">
          <div class="custom-control custom-radio">
            <input
              :disabled="sendFundersDisable"
              :id="row.id"
              v-model="selectInvoiceField"
              :value="row.id"
              type="radio"
              class="custom-control-input"
            />
            <label :for="row.id" class="custom-control-label" />
          </div>
        </template>
      </table-column>
      <table-column
        v-if="userType == 'funder'"
        :label="$t('invoices.financing_request_date')"
        sort-as="prepayment_date"
      >
        <template slot-scope="row">
          <p>{{ row.pre_payment_request.formattedPrePaymentDate }}</p>
        </template>
      </table-column>
      <table-column
        v-else
        :label="$t('invoices.date')"
        sort-as="invoice_date"
        show="formattedInvoiceDate"
      />
      <table-column
        v-if="userType == 'funder'"
        :label="$t('invoices.promised_payment_date')"
        sort-as="promised_date"
      >
        <template slot-scope="row">
          <p>
            {{ row.pre_payment_request.invoice.formattedPromisedPaymentDate }}
          </p>
        </template>
      </table-column>
      <table-column
        v-else
        :label="$t('invoices.due_date')"
        sort-as="due_date"
        show="formattedDueDate"
      />
      <table-column
        v-if="
          userType === 'super_admin' ||
            userType === 'supplier' ||
            userType === 'funder'
        "
        :label="$t('invoices.customer')"
        width="20%"
      >
        <template slot-scope="row">
          <p v-if="row.buyer_stakeholder">
            <span
              v-if="
                userType == 'supplier' &&
                  row.customer.parent_account_id &&
                  row.buyer_stakeholder.details.company &&
                  row.buyer_stakeholder.details.company.name
              "
            >
              {{ row.buyer_stakeholder.details.company.name }}
            </span>
            <span
              v-else-if="
                userType == 'supplier' && row.customer.parent_account_id == null
              "
            >
              {{ row.buyer_stakeholder.details.user.name }}
            </span>
            <!-- {{row.buyer_stakeholder.details.user.name}} -->
            <span v-else>
              {{ row.buyer_stakeholder.details.company.name }}
            </span>
          </p>
        </template>
      </table-column>

      <table-column
        v-if="
          userType === 'super_admin' ||
            userType === 'anchor' ||
            userType === 'funder'
        "
        :label="$t('invoices.supplier')"
      >
        <template slot-scope="row">
          <p v-if="row.supplier_stakeholder">
            <span
              v-if="
                row.supplier_stakeholder.details.company &&
                  row.supplier_stakeholder.details.company.name
              "
            >
              {{ row.supplier_stakeholder.details.company.name }}
            </span>
          </p>
        </template>
      </table-column>
      <table-column
        v-if="userType != 'super_admin'"
        :label="$t('invoices.status')"
        sort-as="status"
      >
        <template v-if="row.overall_status" slot-scope="row">
          <span> {{ $t("invoices.status") }}</span>
          <span
            v-if="userType == 'anchor' && row.overall_status == 'SENT'"
            :class="'inv-status-' + row.overall_status.toLowerCase()"
            >{{ $t("general.received").toUpperCase() }}</span
          >
          <span
            v-else-if="userType == 'anchor' && row.overall_status != 'SENT'"
            :class="'inv-status-' + row.overall_status.toLowerCase()"
            >{{
              row.overall_status != "PARTIALLY_PAID"
                ? row.overall_status
                : row.overall_status.replace("_", " ")
            }}</span
          >
          <span
            v-else-if="userType != 'anchor'"
            :class="'inv-status-' + row.overall_status.toLowerCase()"
            >{{
              row.overall_status != "PARTIALLY_PAID"
                ? row.overall_status
                : row.overall_status.replace("_", " ")
            }}</span
          >
        </template>
      </table-column>

      <table-column
        :label="$t('invoices.invoice_number')"
        show="invoice_number"
      />

      <table-column
        :label="$t('invoices.ref_number')"
        show="reference_number"
      />
      <table-column :label="$t('invoices.amount')" sort-as="total">
        <template slot-scope="row">
          <span>{{ $t("invoices.amount") }}</span>
          <div
            v-html="
              $utils.formatMoney(row.nominal_amount, row.customer.currency)
            "
          />
        </template>
      </table-column>
      <table-column :label="$t('invoices.prePayment')" sort-as="request_level">
        <template slot-scope="row">
          <span> {{ $t("invoices.prePayment") }}</span>
          <span
            v-if="row.request_level == 'Pending' && row.funder_invoices.user"
            :class="'inv-status-' + row.request_level.toLowerCase()"
          >
            {{ row.request_level }}-{{ row.funder_invoices.user.name }}</span
          >
          <span
            v-else-if="row.request_level == 'Financed'"
            :class="'inv-status-' + row.request_level.toLowerCase()"
            >{{ $t("general.financing_approved") }}</span
          >
          <span v-else :class="'inv-status-' + row.request_level.toLowerCase()">
            {{ row.request_level }}</span
          >
        </template>
      </table-column>
    </table-component>
  </div>
</template>
<script>
import { mapActions, mapGetters } from "vuex";
import MultiSelect from "vue-multiselect";
import { validationMixin } from "vuelidate";
import SingleIslamicFinanceRow from "./SingleIslamicFinanceRow.vue";
import SingleFunderRow from "../funders/SingleFunderRow.vue";
const {
  required,
  between,
  maxLength,
  numeric
} = require("vuelidate/lib/validators");

export default {
  components: {
    MultiSelect,
    SingleIslamicFinanceRow,
    SingleFunderRow
  },
  mixins: [validationMixin],
  computed: {
    ...mapGetters("OfferToSell", [
      "offerToSells",
      "selectedOfferToSells",
      "totalOfferToSells",
      "selectAllFieldAttachment",
      "attachments",
      "totalAttachments",
      "selectedAttachments",
      "selectedPreapprovedInvoice"
    ]),
    ...mapGetters("islamicFinanceForm", ["islamicFinanceForm"]),
    showEmptyScreen() {
      return !this.fetchPreapprovedInvoiceData && !this.isRequestOngoing;
    },
    offerAcceptForm() {
      return this.islamicFinanceForm.find(x => x.name === "Offer Accepted");
    },
    selectAllFieldStatus: {
      get: function() {
        return this.selectAllFieldAttachment;
      },
      set: function(val) {
        this.setSelectAllState(val);
      }
    },
    selectInvoiceField: {
      get: function() {
        return this.selectedPreapprovedInvoice;
      },
      set: function(val) {
        this.selectPreapprovedInvoice(val);
      }
    }
  },
  data() {
    return {
      sendFundersDisable: false,
      verificationDisable: false,
      userType: null,
      fetchData: [],
      actionRecord: [],
      fetchDataFunder: [],
      isLoading: false,
      customer_id: null,
      reference_number: null,
      isRequestOngoing: true
    };
  },
  validations() {
    return {};
  },
  created() {
    if (localStorage.getItem("role") === "super_admin") {
      this.userType = "super_admin";
    } else if (localStorage.getItem("account_type") === "anchor") {
      this.userType = "anchor";
    } else if (localStorage.getItem("account_type") === "supplier") {
      this.userType = "supplier";
      this.loadFunder();
    } else if (localStorage.getItem("account_type") === "funder") {
      this.userType = "funder";
    }
    this.loadFinance();
  },
  destroyed() {
    if (this.selectAllField) {
      this.selectAllInvoices();
    }
  },
  methods: {
    ...mapActions("invoice", [
      "fetchAllInvoices",
      "fetchInvoices",
      "getRecord",
      "selectInvoice",
      "resetSelectedInvoices",
      "selectAllInvoices",
      "deleteInvoice",
      "paymentMultipleInvoices",
      "sendEmail",
      "markAsSent",
      "setSelectAllState",
      "cloneInvoice",
      "FetchInvoiceSuppliers",
      "funderInvoices",
      "rejectedInvoice",
      "blockedInvoice",
      "unblockedInvoice"
    ]),
    ...mapActions("OfferToSell", [
      "SingleIslamicFinanceArray",
      "deleteAttachment",
      "fetchAttachments",
      "selectAllAttachments",
      "setSelectAllState",
      "selectAttachment",
      "deleteMultipleAttachments",
      "markAsManuallyInvoiceVerification",
      "selectPreapprovedInvoice"
    ]),
    ...mapActions("funder", ["singleFunderArray"]),
    ...mapActions("modal", ["openModal"]),
    async fetchPreapprovedInvoiceData({ page, per_page, filter, sort }) {
      if (this.customer_id) {
        let data = {
          type: "preapproved",
          customer_id: this.customer_id,
          reference_number: this.reference_number,
          funder_id: this.$route.params.funder_id,
          orderByField: sort.fieldName || "created_at",
          orderBy: sort.order || "desc",
          per_page, // Add rowsPerPage in the API request
          page
        };
        this.isRequestOngoing = true;
        let response = await this.fetchInvoices(data);

        this.isRequestOngoing = false;
        this.currency = response.data.currency;

        return {
          data: response.data.invoices.data,
          pagination: {
            totalPages: response.data.invoices.last_page,
            currentPage: page,
            count: response.data.invoices.count,
            rowsPerPage: response.data.invoices.per_page // Add rowsPerPage in the API request
          }
        };
      }
      return {
        data: [],
        pagination: {
          totalPages: 0,
          currentPage: page,
          count: 0,
          rowsPerPage: per_page
        }
      };
    },

    async saveInvoiceInfo() {
      this.isLoading = true;
      const data = {
        islamic_finance_id: this.$route.params.id,
        invoice_id: this.selectedPreapprovedInvoice
      };
      let response = await this.markAsManuallyInvoiceVerification(data);
      if (response.data.success) {
        window.toastr["success"](
          this.$t("islamic_finance.invoice_verification_successfully")
        );
        this.$router.push(
          `/supplier/funders/${this.$route.params.funder_id}/islamic_finance`
        );
        this.isLoading = false;
      } else {
        this.isLoading = false;
      }
    },
    async loadFunder() {
      let response = await this.singleFunderArray(this.$route.params.funder_id);
      this.fetchDataFunder = response.data.account;
    },
    refreshTable(val) {
      this.$refs.table.refresh();
    },
    async loadFinance() {
      let response = await this.SingleIslamicFinanceArray(
        this.$route.params.id
      );
      if (response.data) {
        this.fetchData = response.data.islamicFinance;
        this.actionRecord =
          response.data.islamicFinance[0].islamic_finance_status;
        this.customer_id = response.data.islamicFinance[0].customer_id;
        this.reference_number = response.data.islamicFinance[0].invoice_number;
        this.refreshTable();
      }
    }
  }
};
</script>
<style scoped>
.table-component {
  min-height: 0px !important;
}
</style>
